import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { TrackMap } from '../components/maps/OsmMap';
import { useRealtime } from '../lib/realtime';
import { fmtEur } from '../lib/constants';
import {
  Bike, Moon, Sun, LogOut, MapPin, Phone, Navigation, ArrowLeft,
  Play, BadgeCheck, RadioTower, Banknote, CreditCard, ChevronRight, Package,
} from 'lucide-react';

export default function CourierApp() {
  const { t } = useTranslation();
  const { user, logout, setUiLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [scope, setScope] = useState('active');
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [restGeo, setRestGeo] = useState(null);
  const [code, setCode] = useState('');
  const [broadcastId, setBroadcastId] = useState(null);
  const [myGeo, setMyGeo] = useState(null);
  const watchRef = useRef(null);
  const lastSent = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/offsite/courier/orders', { params: { scope } });
      setOrders(data);
    } catch (e) { /* */ }
  }, [scope]);

  useEffect(() => { load(); const tm = setInterval(load, 8000); return () => clearInterval(tm); }, [load]);
  useEffect(() => { api.get('/offsite/courier/orders', { params: { scope: 'active' } }).then(() => {}).catch(() => {}); }, []);
  useRealtime((msg) => { if (msg && msg.event === 'offsite_orders') load(); });

  const selected = orders.find((o) => o.id === selectedId) || null;

  // Try to resolve a sensible restaurant pin from any order (not exposed on courier list);
  // fallback: none. We still show customer + courier markers.
  useEffect(() => { setRestGeo(null); }, [selectedId]);

  // GPS broadcasting while a delivery is in transit.
  useEffect(() => {
    if (!broadcastId) {
      if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; }
      return undefined;
    }
    if (!('geolocation' in navigator)) { toast.error('GPS non disponibile'); return undefined; }
    watchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const geo = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMyGeo(geo);
        const now = Date.now();
        if (now - lastSent.current > 4000) {
          lastSent.current = now;
          try { await api.post(`/offsite/courier/orders/${broadcastId}/location`, geo); } catch (e) { /* */ }
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    );
    return () => { if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } };
  }, [broadcastId]);

  const startDelivery = async (o) => {
    try {
      await api.post(`/offsite/courier/orders/${o.id}/start`);
      setBroadcastId(o.id);
      toast.success(t('offsite.courier.gpsActive'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const confirmDelivery = async (o) => {
    try {
      await api.post(`/offsite/courier/orders/${o.id}/confirm`, { code: code.trim() });
      setBroadcastId(null); setCode(''); setSelectedId(null);
      toast.success(t('offsite.courier.delivered'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('offsite.courier.wrongCode')); }
  };

  const navLink = (geo) => `https://www.google.com/maps/dir/?api=1&destination=${geo.lat},${geo.lng}`;

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <header className="h-14 shrink-0 border-b border-border bg-card flex items-center gap-2 px-4 sticky top-0 z-30">
        {selected ? (
          <Button variant="ghost" size="icon" onClick={() => setSelectedId(null)} data-testid="courier-back-button"><ArrowLeft className="h-5 w-5" /></Button>
        ) : (
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Bike className="h-5 w-5 text-primary-foreground" /></div>
        )}
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm leading-tight truncate">{selected ? t('offsite.board.details') : t('offsite.courier.title')}</div>
          <div className="text-xs text-muted-foreground leading-tight truncate">{user?.name}</div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {broadcastId && <Badge className="gap-1 bg-pos-free text-white"><RadioTower className="h-3 w-3" /> GPS</Badge>}
          <LanguageSwitcher variant="compact" onChange={(code) => setUiLanguage(code)} />
          <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</Button>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="courier-logout-button" className="text-destructive"><LogOut className="h-5 w-5" /></Button>
        </div>
      </header>

      <div className="flex-1 overflow-auto">
        {!selected ? (
          <div className="p-4 space-y-3" data-testid="courier-assigned-list">
            <Tabs value={scope} onValueChange={setScope}>
              <TabsList className="w-full">
                <TabsTrigger value="active" className="flex-1">{t('offsite.courier.active')}</TabsTrigger>
                <TabsTrigger value="done" className="flex-1">{t('offsite.courier.done')}</TabsTrigger>
              </TabsList>
            </Tabs>
            {orders.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground text-sm">{t('offsite.courier.empty')}</div>
            ) : orders.map((o) => (
              <Card key={o.id} className="p-3.5 cursor-pointer hover:shadow-md-token transition-shadow" data-testid="courier-order-card" onClick={() => setSelectedId(o.id)}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm flex items-center gap-1.5"><Package className="h-4 w-4 text-primary" />{(o.customer || {}).name}</div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2 flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />{o.address}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <Badge variant="outline" className="tabular text-[11px]">{o.time_slot === 'asap' ? t('offsite.asap') : o.time_slot}</Badge>
                      <Badge variant="secondary" className="gap-1 text-[11px]">{(o.payment || {}).method === 'card' ? <CreditCard className="h-3 w-3" /> : <Banknote className="h-3 w-3" />}{fmtEur(o.total)}</Badge>
                      {o.status === 'in_transit' && <Badge className="bg-primary text-[11px]">{t('offsite.status.in_transit')}</Badge>}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="flex flex-col" data-testid="courier-delivery-detail">
            {selected.geo && (
              <TrackMap restaurant={restGeo} customer={selected.geo} courier={myGeo || selected.courier_geo}
                className="h-[40vh] w-full border-b" />
            )}
            <div className="p-4 space-y-4">
              <div className="rounded-xl border bg-card p-3 space-y-1.5 text-sm">
                <div className="font-display font-bold">{(selected.customer || {}).name}</div>
                <a href={`tel:${(selected.customer || {}).phone}`} className="flex items-center gap-2 text-primary"><Phone className="h-4 w-4" />{(selected.customer || {}).phone}</a>
                <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />{selected.address}</div>
                {selected.notes && <div className="text-muted-foreground italic">“{selected.notes}”</div>}
                <div className="flex items-center gap-2 pt-1">
                  <Badge variant="secondary" className="gap-1">{(selected.payment || {}).method === 'card' ? <CreditCard className="h-3 w-3" /> : <Banknote className="h-3 w-3" />}{t(`offsite.${(selected.payment || {}).method === 'card' ? 'card' : 'cash'}`)} · {fmtEur(selected.total)}</Badge>
                  {(selected.payment || {}).cash_change != null && <span className="text-xs text-muted-foreground">{t('offsite.board.change')} {fmtEur(selected.payment.cash_change)}</span>}
                </div>
              </div>

              {selected.geo && (
                <a href={navLink(selected.geo)} target="_blank" rel="noreferrer" data-testid="courier-open-navigation-link">
                  <Button variant="outline" className="w-full rounded-xl"><Navigation className="h-4 w-4 mr-2" />{t('offsite.courier.navigate')}</Button>
                </a>
              )}

              {selected.status === 'ready' && (
                <Button className="w-full rounded-xl h-12" onClick={() => startDelivery(selected)} data-testid="courier-start-delivery-button">
                  <Play className="h-5 w-5 mr-2" />{t('offsite.courier.start')}
                </Button>
              )}

              {selected.status === 'in_transit' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-pos-free font-medium"><RadioTower className="h-4 w-4" />{t('offsite.courier.gpsActive')}</div>
                  <div>
                    <label className="text-sm font-medium">{t('offsite.courier.enterCode')}</label>
                    <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={4}
                      placeholder="0000" className="mt-1.5 text-center text-2xl font-mono tracking-widest h-14" data-testid="courier-confirmation-code-input" />
                  </div>
                  <Button className="w-full rounded-xl h-12" disabled={code.trim().length < 4} onClick={() => confirmDelivery(selected)} data-testid="courier-confirm-delivered-button">
                    <BadgeCheck className="h-5 w-5 mr-2" />{t('offsite.courier.confirm')}
                  </Button>
                </div>
              )}

              {selected.status === 'delivered' && (
                <div className="flex items-center justify-center gap-2 text-pos-free font-semibold py-3"><BadgeCheck className="h-5 w-5" />{t('offsite.courier.delivered')}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
